import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AuthLayout } from '@/components/AuthLayout';
import { Button, Field, Input } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';

/* -------------------------------------------------------------------------
 * Requesting a reset link.
 *
 * ⚠️ `POST /auth/forgot-password` ALWAYS answers 204. It does not say whether
 * the address has an account, deliberately — the endpoint is an
 * enumeration-free oracle, and the copy is the half of it that leaks. So the
 * confirmation below is worded to be TRUE in both cases: it never says we
 * found the account ("check your inbox, a link is on its way to you"), and it
 * never says we did not ("no account with that address"). Either sentence
 * turns this form into an account-existence check that anybody can run.
 *
 * The corollary that is easy to get wrong: a NETWORK failure must NOT show the
 * same confirmation. A request that did not happen is not a request that
 * succeeded — telling somebody a link is on its way when nothing reached the
 * server leaves them waiting for an email nobody was asked to send. Only a
 * resolved call reaches `setSent(true)`.
 *
 * Reached with no session, so nothing here may touch `useAuth()`.
 * --------------------------------------------------------------------- */

// A neutral TextLink, not accent: navy is reserved for primary actions.
const BACK_LINK = 'font-medium';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [sent, setSent] = useState(false);

  /*
   * The in-flight request, so unmounting mid-submit aborts it rather than
   * setting state on a page that is gone. A ref rather than an effect-created
   * controller because the request is started by a submit, not by a render.
   */
  const inFlight = useRef<AbortController | null>(null);
  useEffect(() => () => inFlight.current?.abort(), []);

  const fieldErrors = error instanceof ApiError ? error.fieldErrors : {};
  /*
   * Ordered by specificity, not by which shape arrived first: a message bound
   * to `email` renders on the field, a form-level refusal (an empty path, or a
   * `.strict()` unrecognised key) renders in the banner, and the raw message is
   * the last resort — on a 422 it is only ever "Validation failed".
   */
  const formMessage = error instanceof ApiError ? error.formMessage : null;
  const bannerMessage =
    formMessage ?? (Object.keys(fieldErrors).length > 0 ? null : (error?.message ?? null));

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    // Belt as well as braces: the button is disabled while in flight, but a
    // second Enter before React repaints would otherwise fire a second request.
    if (submitting) return;

    const controller = new AbortController();
    inFlight.current = controller;
    const { signal } = controller;

    setError(null);
    setSubmitting(true);
    try {
      await api.forgotPassword({ email: email.trim() }, signal);
      if (signal.aborted) return;
      setSent(true);
    } catch (caught) {
      if (signal.aborted) return;
      setError(caught instanceof Error ? caught : new Error('Something went wrong'));
    } finally {
      if (!signal.aborted) setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout title="Check your email">
        <div className="space-y-4">
          {/*
           * True whether or not that address has an account — see the header.
           * The second sentence exists because the first one is deliberately
           * non-committal, and without it a reader who gets no email has no
           * way to tell a wrong address from a slow one.
           */}
          <p className="m-0 text-sm text-fg-secondary">
            If <span className="font-medium text-fg [overflow-wrap:anywhere]">{email.trim()}</span>{' '}
            has an account, a reset link is on its way. Single use, and it expires.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setSent(false);
                setError(null);
              }}
            >
              Use a different address
            </Button>
          </div>

          <p className="m-0 text-xs text-fg-subtle">
            <TextLink to="/login" className={BACK_LINK}>
              Back to sign in
            </TextLink>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field label="Work email" required error={fieldErrors.email}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              autoFocus
              invalid={Boolean(fieldErrors.email)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        {bannerMessage && (
          <Notice tone="danger" role="alert" className="break-words">
            {bannerMessage}
          </Notice>
        )}

        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          disabled={submitting || email.trim() === ''}
          className="w-full"
        >
          Send reset link
        </Button>

        <p className="m-0 text-center text-xs text-fg-subtle">
          <TextLink to="/login" className={BACK_LINK}>
            Back to sign in
          </TextLink>
        </p>
      </form>
    </AuthLayout>
  );
}
